import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormatter } from 'utils/grid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  calculateCompensationInEuros,
  sumInEuros
} from 'components/roster-portal/home/travel-expense-claims/utils';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

export const useColumnDefinitions = ({
  showEmployeeName = false
}: {
  showEmployeeName?: boolean;
}) => {
  const { t } = useTranslation('common');

  return useMemo(
    () =>
      [
        {
          field: 'employmentNumber',
          type: 'string',
          headerName: t('travelMovements.employmentNumber'),
          valueGetter: (params) => params.row?.employment?.number ?? '',
          minWidth: 100,
          maxWidth: 150,
          flex: 1
        },
        ...(showEmployeeName
          ? [
              {
                field: 'employeeName',
                type: 'string',
                headerName: t('employee'),
                valueGetter: (params) =>
                  params.row?.employment?.employeeName ?? '',
                minWidth: 120,
                flex: 1
              }
            ]
          : []),
        {
          field: 'date',
          type: 'date',
          headerName: t('date'),
          valueFormatter: dateFormatter,
          maxWidth: 120,
          flex: 1
        },
        {
          field: 'travelDistancePrivate',
          headerName: t('travelMovements.distancePrivate'),
          valueGetter: (params) => {
            const routes = params.row.travelMovements || [];
            const distanceInMeters = routes.reduce(
              (acc, route) =>
                route.type === 'PRIVATE'
                  ? acc +
                    (route.correctedDistanceInMeters
                      ? route.correctedDistanceInMeters
                      : route.distanceInMeters || 0)
                  : acc,
              0
            );
            return distanceInMeters;
          },
          valueFormatter: (params) => {
            if (params.value === 0 || params.value == null) {
              return '';
            }
            const distanceInKm = params.value / 1000;
            return Number.isInteger(distanceInKm)
              ? distanceInKm
              : distanceInKm.toFixed(2).replace('.', ',');
          },
          type: 'number',
          maxWidth: 180,
          align: 'left',
          headerAlign: 'left',
          flex: 1
        },
        {
          field: 'travelDistanceBusiness',
          headerName: t('travelMovements.distanceBusiness'),
          valueGetter: (params) => {
            const routes = params.row.travelMovements || [];
            const isManual = params.row.isManual;
            const commuteDistanceInMeters =
              params.row.commuteDistanceInMeters || 0;

            const manualDistanceInMeters = isManual
              ? routes.reduce(
                  (acc, route) =>
                    params.row.isManual
                      ? acc +
                        (route.correctedDistanceInMeters
                          ? route.correctedDistanceInMeters
                          : route.distanceInMeters || 0)
                      : acc,
                  0
                )
              : 0;

            const distanceInMeters = isManual
              ? Math.max(manualDistanceInMeters - commuteDistanceInMeters, -1)
              : routes.reduce(
                  (acc, route) =>
                    route.type === 'BUSINESS'
                      ? acc + (route.distanceInMeters || 0)
                      : acc,
                  0
                );
            return distanceInMeters;
          },
          valueFormatter: (params) => {
            const value = params.value;
            if (value == null || value === 0) return '';
            if (value === -1) return '0';
            const distanceInKm = params.value / 1000;
            return Number.isInteger(distanceInKm)
              ? distanceInKm
              : distanceInKm.toFixed(2).replace('.', ',');
          },
          type: 'number',
          maxWidth: 180,
          align: 'left',
          headerAlign: 'left',
          flex: 1
        },
        {
          field: 'transportationMode',
          type: 'string',
          headerName: t('travelDistances.transportationMode'),
          valueGetter: (params) => {
            const mode = params.row.transportationMode || '';
            return t(`travelDistances.transportationModes.${mode}`);
          },
          maxWidth: 160,
          flex: 1
        },
        {
          field: 'description',
          type: 'string',
          headerName: t('description'),
          flex: 1,
          minWidth: 200
        },
        {
          field: 'statusNote',
          type: 'string',
          headerName: t('travelExpenseClaims.statusNote'),
          flex: 1,
          minWidth: 200
        },
        {
          field: 'currentStatus',
          type: 'string',
          headerName: t('status'),
          valueGetter: (params) => {
            const status = params.row.status || '';
            return t(`travelMovements.status.${status}`);
          },
          minWidth: 90,
          maxWidth: 200,
          flex: 1
        },
        {
          field: 'amountInEuros',
          type: 'number',
          headerName: t('travelDistances.compensation'),
          valueGetter: (params) => {
            const routes = params.row.travelMovements || [];
            const compensation = calculateCompensationInEuros(routes, true);
            return compensation || '';
          },
          valueFormatter: (params) =>
            params.value && `${sumInEuros(params.value)}`,
          minWidth: 90,
          maxWidth: 200,
          flex: 1,
          headerAlign: 'left',
          align: 'left'
        },
        {
          field: 'isManual',
          type: 'string',
          headerName: t('travelMovements.isManual'),
          flex: 1,
          minWidth: 90,
          renderCell: BooleanCell
        }
        // TODO: value for transportationTypeName is not available in TravelMovementResponse it is always NULL.
        // TODO: requires a fix in the backend
        // {
        //   field: 'transportationTypeName',
        //   type: 'string',
        //   headerName: t('transportationType'),
        //   valueFormatter: (params) => {
        //     const transportationType =
        //       params.value as TransportationTypeResponse;
        //     return transportationType ? transportationType.name : '';
        //   },
        //   minWidth: 150
        // }
      ] as GridColDef[],
    [t]
  );
};

export default useColumnDefinitions;
